import { runPlatformHomePrefetch, clearPlatformHomePrefetch } from './platformHomeFetch';

// RD-PERF-003 — memory-only stale-while-revalidate stores for the PLATFORM
// (super-admin) console: home lists (tenants + platform users), the signed-in
// platform user's own profile (timezone + name) and the 24h login-failure
// count. Single slot per store, keyed by the platform user's id (session user,
// never a URL/prop) so one session's data can never be read under another
// user. NEVER persisted (no localStorage/IndexedDB); cleared at sign-out in
// BOTH clearPerUserDeviceState and clearSignedOutMemoryState. An `epoch`
// bump on clear makes any in-flight response from before the sign-out
// discard itself instead of repopulating the store.
// `supabase` is imported lazily inside the two prefetchers so the pure readers
// (peek / subscribe / isResolved) stay importable without the client (Jest).
const loadSupabase = async () => (await import('../../supabase')).supabase;

let epoch = 0;
const listeners = new Set();

function notify() {
  [...listeners].forEach((fn) => { try { fn(); } catch (_) { /* never break a reader */ } });
}

export function subscribePlatformSession(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function getPlatformSessionEpoch() {
  return epoch;
}

// ── Home lists ─────────────────────────────────────────────────────────────
let home = { userId: null, tenants: null, platformUsers: null };

/** Last known `{ tenants, platformUsers }` (either may be null) for this user, any age. */
export function peekPlatformHome(userId) {
  if (!userId || home.userId !== userId) return { tenants: null, platformUsers: null };
  return { tenants: home.tenants, platformUsers: home.platformUsers };
}

function homeSlotFor(userId) {
  if (home.userId !== userId) home = { userId, tenants: null, platformUsers: null };
  return home;
}

/**
 * RD-PLAT-016 — the plan catalog + feature switches that ride the companies
 * list payload (`plans[]`, `features[]`). Kept beside, not inside, the home
 * lists so `peekPlatformHome`'s `{ tenants, platformUsers }` shape is unchanged.
 */
export function peekPlatformCatalog(userId) {
  if (!userId || home.userId !== userId || !home.catalog) return null;
  return home.catalog;
}

export function setPlatformHomeCatalog(userId, catalog, atEpoch = epoch) {
  if (!userId || atEpoch !== epoch) return;
  homeSlotFor(userId).catalog = catalog && typeof catalog === 'object' ? catalog : null;
}

/** `atEpoch` = epoch captured when the request started; stale responses are dropped. */
export function setPlatformHomeTenants(userId, tenants, atEpoch = epoch) {
  if (!userId || atEpoch !== epoch) return;
  homeSlotFor(userId).tenants = Array.isArray(tenants) ? tenants : [];
}

export function setPlatformHomeUsers(userId, platformUsers, atEpoch = epoch) {
  if (!userId || atEpoch !== epoch) return;
  homeSlotFor(userId).platformUsers = Array.isArray(platformUsers) ? platformUsers : [];
}

/** Keeps the cache in step with in-page edits (AI toggle, deactivate…) — only if already cached. */
export function syncPlatformHomeTenants(userId, tenants) {
  if (userId && home.userId === userId && home.tenants) home.tenants = tenants;
}

export function syncPlatformHomeUsers(userId, platformUsers) {
  if (userId && home.userId === userId && home.platformUsers) home.platformUsers = platformUsers;
}

/**
 * KAN-709 — sign-in prefetch of both home lists (called once 2FA is accepted).
 * Dedupes per user; a sign-out (epoch bump) clears the entry and the hook then
 * refuses it. Never rejects. The hook adopts the result via
 * `takePlatformHomePrefetch`; edge fns still enforce super-admin + aal2.
 */
export function prefetchPlatformHome(userId, startEpoch = epoch) {
  if (!userId || startEpoch !== epoch) return Promise.resolve(); // signed out before it started
  return runPlatformHomePrefetch(userId, startEpoch, () => startEpoch === epoch);
}

// ── Platform user's own profile (timezone + display name) ──────────────────
let profile = {
  userId: null, timezone: null, fullName: null, ok: false, resolved: false,
};
let profileInflight = null; // { userId, promise }

/** `{ timezone, fullName, ok }` or null when nothing is known for this user. */
export function peekPlatformProfile(userId) {
  if (!userId || profile.userId !== userId) return null;
  return { timezone: profile.timezone, fullName: profile.fullName, ok: profile.ok };
}

/** One fetch per user: dedupes in flight, skips when already loaded OK. Never rejects. */
export function prefetchPlatformProfile(userId) {
  if (!userId) return Promise.resolve();
  if (profile.userId === userId && profile.ok) return Promise.resolve();
  if (profileInflight?.userId === userId) return profileInflight.promise;
  const startEpoch = epoch;
  const promise = (async () => {
    // A failed fetch settles `resolved` but must never overwrite a timezone/name
    // already known for this user (e.g. set by setPlatformTimezone).
    const known = profile.userId === userId ? profile : null;
    let next = {
      userId,
      timezone: known?.timezone ?? null,
      fullName: known?.fullName ?? null,
      ok: false,
      resolved: true,
    };
    try {
      const supabase = await loadSupabase();
      const { data, error } = await supabase
        .from('platform_users')
        .select('timezone, full_name')
        .eq('user_id', userId)
        .maybeSingle();
      if (!error) {
        next = {
          userId,
          timezone: data?.timezone || null,
          fullName: data?.full_name?.trim() || null,
          ok: true,
          resolved: true,
        };
      }
    } catch (_) { /* fall through: resolved without a value */ }
    if (startEpoch === epoch) {
      // A failed fetch keeps whatever is in the slot NOW (a concurrent
      // setPlatformTimezone may have written a timezone) and only settles it.
      profile = next.ok || profile.userId !== userId ? next : { ...profile, resolved: true };
      if (profileInflight?.userId === userId) profileInflight = null;
      notify();
    }
  })();
  profileInflight = { userId, promise };
  return promise;
}

/** The loaded platform user's timezone, or null when no platform session is loaded
 *  (tenant users never load one). Single slot, cleared at sign-out. */
export function peekLoadedPlatformTimezone() {
  return profile.timezone || null;
}

/** True once the first fetch for this user has SETTLED (any outcome) — not merely
 *  once a timezone was written for the user. */
export function isPlatformProfileResolved(userId) {
  return Boolean(userId && profile.userId === userId && profile.resolved);
}

export function setPlatformTimezone(userId, timezone) {
  if (!userId) return;
  if (profile.userId !== userId) {
    profile = {
      userId, timezone, fullName: null, ok: false, resolved: false,
    };
  }
  else profile = { ...profile, timezone };
  notify();
}

// ── 24h login-failure count ────────────────────────────────────────────────
let failures = { userId: null, count: 0, resolved: false };
let failuresInflight = null;

export function peekPlatformLoginFailures(userId) {
  if (!userId || failures.userId !== userId) return null;
  return failures.count;
}

/** Resolved-ness of the single slot; no id needed (the slot only ever holds the session user). */
export function isPlatformLoginFailuresResolved() {
  return failures.resolved;
}

/** Refreshes silently; dedupes in flight. Fail-soft: errors read as 0. Never rejects. */
export function prefetchPlatformLoginFailures(userId) {
  if (!userId) return Promise.resolve();
  if (failuresInflight?.userId === userId) return failuresInflight.promise;
  const startEpoch = epoch;
  const promise = (async () => {
    let count = 0;
    try {
      // Rolling 24h window — Date.now is correct (not a tenant calendar-day boundary).
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const supabase = await loadSupabase();
      const { count: c, error } = await supabase
        .from('platform_login_failures')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', since);
      count = error ? 0 : (c ?? 0);
    } catch (_) { count = 0; }
    if (startEpoch === epoch) {
      failures = { userId, count, resolved: true };
      if (failuresInflight?.userId === userId) failuresInflight = null;
      notify();
    }
  })();
  failuresInflight = { userId, promise };
  return promise;
}

/** Sign-out: forget everything and orphan any in-flight response. */
export function clearPlatformSessionMemory() {
  epoch += 1;
  clearPlatformHomePrefetch();
  home = { userId: null, tenants: null, platformUsers: null };
  profile = {
    userId: null, timezone: null, fullName: null, ok: false, resolved: false,
  };
  profileInflight = null;
  failures = { userId: null, count: 0, resolved: false };
  failuresInflight = null;
  notify();
}
